// v236 admin API client for the in-app API playground (item 55 Phase
// D / item 64 in the recommended delivery order of
// PRODUCT-ROADMAP.md). Backs the AdminApiReference page that ships
// the master toggle plus the operation-browser surface gated behind
// it.

import useSWR from 'swr';

export interface APIPlaygroundSettings {
  enabled: boolean;
  updated_at: string;
  updated_by?: string;
}

export interface APIOperationParameter {
  name: string;
  in: 'path' | 'query' | 'header' | 'cookie';
  required?: boolean;
  description?: string;
  type?: string;
}

export interface APIOperationResponse {
  status: string;
  description?: string;
  content_type?: string;
}

export interface APIOperation {
  tag: string;
  method: string;
  path: string;
  operation_id?: string;
  summary?: string;
  description?: string;
  deprecated?: boolean;
  parameters?: APIOperationParameter[];
  has_body?: boolean;
  body_type?: string;
  body_description?: string;
  responses?: APIOperationResponse[];
}

export interface APIOperationListing {
  operations: APIOperation[];
  version: string;
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useAPIPlaygroundSettings() {
  return useSWR<APIPlaygroundSettings>(
    '/api/admin/api-playground/settings',
    json,
    { refreshInterval: 30_000 },
  );
}

// useAPIPlaygroundOperations is gated on the toggle being ON — the
// caller passes `enabled` (the resolved settings.enabled) and SWR
// short-circuits on a null key when the toggle is off, so we never
// trip the backend's 403 in the disabled state.
export function useAPIPlaygroundOperations(enabled: boolean) {
  return useSWR<APIOperationListing>(
    enabled ? '/api/admin/api-playground/operations' : null,
    json,
    { revalidateOnFocus: false },
  );
}

export async function updateAPIPlaygroundSettings(
  input: Pick<APIPlaygroundSettings, 'enabled'>,
): Promise<void> {
  const res = await fetch('/api/admin/api-playground/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}
